import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { MAX_INSURER_ROWS } from '@/constants';
import { SERVICE_TYPE_A2, SERVICE_TYPE_BOTH, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { dateValueToIso } from '@/utils/dateConverter';
import { z } from 'zod';

const AVAILABLE_TO_INDEFINITE = '2100-12-31';

const dayPickerFormat = parseFormat(JA_JP.format);

/** RA2: 名称カナはカタカナのみ。Backend/RA2: 名前カナの書式が正しくありません。 / 名前カナは100文字以下にしてください。 */
const katakanaOptional = z
  .string()
  .max(100, '施術者氏名カナは100文字以下にしてください。')
  .refine(
    v => !v || v === '' || /^[ァ-ヶｦ-ﾟー 　]+$/u.test(v),
    '施術者氏名カナの書式が正しくありません。'
  )
  .nullable()
  .optional();

// Email validation: example@domain.com with specific prefix/domain rules.
const emailRegex =
  /^[A-Za-z0-9]+(?:[._-][A-Za-z0-9]+)*@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

/** Message constants aligned with backend MassagerCreateRequest / MassagerUpdateRequest and RA2 validation.php */
/**
 * 施術者氏名 (massagers.name): max 50, enforce via UI input cap.
 * Matches the Shinsa sync target column t_recept_base.KANRISYA varchar(50), which is the
 * narrower of the two Shinsa columns this name feeds (the other is MAS_NAME).
 */
export const MASSAGER_NAME_MAX_LENGTH = 50;

const MSG = {
  clinic_required: '治療院は必須です。',
  clinic_location_required: '拠点選択は必須です。',
  // 施術者名称 (名前) validation
  name_required: '施術者氏名は必須です。',
  name_max: '施術者氏名は50文字以下にしてください。',
  // 施術者ログインID
  login_id_required: '施術者ログインIDは必須です。',
  login_id_max: '施術者ログインIDは100文字以下にしてください。',
  // パスワード
  password_required: 'パスワードは必須です。',
  password_max: 'パスワードは100文字以下にしてください。',
  // メールアドレス
  email_format: 'メールアドレスを正しいメールアドレスにしてください。',
  email_max: 'メールアドレスは100文字以下にしてください。',
  acupuncture_required: 'はり免許番号は必須です。',
  moxibustion_required: 'きゅう免許番号は必須です。',
  acupuncture_max: 'はり免許番号は100文字以下にしてください。',
  moxibustion_max: 'きゅう免許番号は100文字以下にしてください。',
  acupuncture_not_string: 'はり免許番号は文字列にしてください。',
  moxibustion_not_string: 'きゅう免許番号は文字列にしてください。',
  // マッサージ指圧師免許番号 (R2) validation
  license_number_max: 'マッサージ指圧師免許番号は100文字以下にしてください。',
  license_number_required: 'マッサージ指圧師免許番号は必須です。',
  // 保険者別番号テーブル
  // 保険者番号 (wide_area_union_code)
  wide_area_code_max: '保険者番号は8文字以下にしてください。',
  // 施術機関コード (wide_area_union_number)
  wide_area_number_max: '施術機関コードは100文字以下にしてください。',
  // 県番号 (prefecture_number)
  prefecture_number_max: '県番号は2文字以下にしてください。',
  wide_area_rows_max: `保険者別番号は${MAX_INSURER_ROWS}件以下で入力してください。`,
  license_exactly_one: 'はり免許番号は必須です。',
  moxibustion_license_required_for_empty: 'きゅう免許番号は必須です。',
  available_from_required: '適用開始日は必須です。',
  available_to_after_from: '適用終了日は適用開始日以降を指定してください。',
  available_to_exceeds_location: '適用終了日は、拠点の適用期間内の日付を入力してください。',
  available_to_before_last_visit:
    'すでに設定済みの期間で訪問入力されています。編集が必要な場合は、対象日付の訪問データを削除してから再度行ってください。',
  /** No649: アップ会員の場合、登録日は請求業務で必須 */
  regist_date_required: '登録日は必須です。',
} as const;

const clinicLocationItem = z.object({
  clinic_location_id: z.number(),
  /** 適用期間: 拠点選択時はenable、locationの値はfillしない */
  available_from: z.string().nullable().optional(),
  available_to: z.string().nullable().optional(),
  /** 登録日・廃止依頼日: per clinic_location, stored in clinic_location_massagers */
  regist_date: z.string().nullable().optional(),
  stop_request_date: z.string().nullable().optional(),
});

const baseFields = {
  clinic_id: z.number({ required_error: MSG.clinic_required }),
  clinic_locations_a2: z.array(clinicLocationItem).default([]),
  clinic_locations_r2: z.array(clinicLocationItem).default([]),
  name: z.string().min(1, MSG.name_required).max(MASSAGER_NAME_MAX_LENGTH, MSG.name_max),
  name_kana: katakanaOptional,
  // 施術者ログインID: 任意入力, max 100
  login_id: z.string().max(100, MSG.login_id_max).optional().or(z.literal('')),
  // パスワード: 任意入力, max 100
  password: z.string().max(100, MSG.password_max).optional().or(z.literal('')),
  // メールアドレス: optional, trim spaces, max 100, custom format
  email: z
    .preprocess(
      v => (typeof v === 'string' ? v.trim() : v),
      z
        .string()
        .max(100, MSG.email_max)
        .refine(v => !v || v === '' || emailRegex.test(v), MSG.email_format)
    )
    .nullable()
    .optional(),
  acupuncture_license_number: z
    .string({ invalid_type_error: MSG.acupuncture_not_string })
    .max(100, MSG.acupuncture_max)
    .nullable()
    .optional(),
  moxibustion_license_number: z
    .string({ invalid_type_error: MSG.moxibustion_not_string })
    .max(100, MSG.moxibustion_max)
    .nullable()
    .optional(),
  // R2: マッサージ指圧師免許番号
  license_number: z.string().max(100, MSG.license_number_max).nullable().optional(),
  wide_area_union_code: z.string().max(8, MSG.wide_area_code_max).nullable().optional(),
  wide_area_union_number: z.string().max(100, MSG.wide_area_number_max).nullable().optional(),
  prefecture_number: z.string().max(2, MSG.prefecture_number_max).nullable().optional(),
  wide_area_union_rows: z
    .array(
      z.object({
        wide_area_union_code: z.string().max(8, MSG.wide_area_code_max).nullable().optional(),
        wide_area_union_number: z.string().max(100, MSG.wide_area_number_max).nullable().optional(),
        prefecture_number: z.string().max(2, MSG.prefecture_number_max).nullable().optional(),
      })
    )
    .max(MAX_INSURER_ROWS, MSG.wide_area_rows_max)
    .nullable()
    .optional(),
  /** R2用（BOTH 時はマッサージ側の保険者別番号） */
  massage_wide_area_union_rows: z
    .array(
      z.object({
        wide_area_union_code: z.string().max(8, MSG.wide_area_code_max).nullable().optional(),
        wide_area_union_number: z.string().max(100, MSG.wide_area_number_max).nullable().optional(),
        prefecture_number: z.string().max(2, MSG.prefecture_number_max).nullable().optional(),
      })
    )
    .max(MAX_INSURER_ROWS, MSG.wide_area_rows_max)
    .nullable()
    .optional(),
};

/** Schema for create/edit massager. A2 | R2 | BOTH (both forms). */
export function createMassagerSchema(
  serviceType: 'A2' | 'R2' | 'BOTH',
  /** No649: When clinic site_type is upsystem, 登録日 is required per 拠点. */
  isUpSystemSite = false,
  clinicLocationOptions: Array<{ id: number; available_to: string }> = [],
  /** Last visit date (YYYY-MM-DD) for the massager. Used for available_to validation in edit mode. */
  lastVisitDate: string | null = null
) {
  return z.object(baseFields).superRefine((data, ctx) => {
    const isA2 = serviceType === SERVICE_TYPE_A2 || serviceType === SERVICE_TYPE_BOTH;
    const isR2 = serviceType === SERVICE_TYPE_R2 || serviceType === SERVICE_TYPE_BOTH;
    const locationsA2 = data.clinic_locations_a2 ?? [];
    const locationsR2 = data.clinic_locations_r2 ?? [];
    const hasValidA2 = locationsA2.some(c => (c?.clinic_location_id ?? 0) > 0);
    const hasValidR2 = locationsR2.some(c => (c?.clinic_location_id ?? 0) > 0);
    const a = (data.acupuncture_license_number ?? '').trim();
    const m = (data.moxibustion_license_number ?? '').trim();
    const l = (data.license_number ?? '').trim();
    const hasA2License = a !== '' || m !== '';
    const hasR2License = l !== '';

    // A2 side: require 拠点 only if there is A2 license
    if (isA2 && hasA2License && !hasValidA2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: MSG.clinic_location_required,
        path: ['clinic_locations_a2'],
      });
    }
    // R2 side: require 拠点 only if there is R2 license
    if (isR2 && hasR2License && !hasValidR2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: MSG.clinic_location_required,
        path: ['clinic_locations_r2'],
      });
    }
    if (isA2 && hasA2License) {
      locationsA2.forEach((item, index) => {
        if ((item?.clinic_location_id ?? 0) <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: MSG.clinic_location_required,
            path: ['clinic_locations_a2', index, 'clinic_location_id'],
          });
        } else {
          const af = (item?.available_from ?? '').toString().trim();
          if (!af) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: MSG.available_from_required,
              path: ['clinic_locations_a2', index, 'available_from'],
            });
          }
          const at = (item?.available_to ?? '').toString().trim();
          if (af && at) {
            const dFrom = parseDate(af, dayPickerFormat);
            const dTo = parseDate(at, dayPickerFormat);
            if (!Number.isNaN(dFrom.getTime()) && !Number.isNaN(dTo.getTime()) && dTo < dFrom) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: MSG.available_to_after_from,
                path: ['clinic_locations_a2', index, 'available_to'],
              });
            }
          }
          if (at) {
            const selectedIso = dateValueToIso(at);
            const location = clinicLocationOptions.find(l => l.id === item.clinic_location_id);
            if (
              location &&
              location.available_to &&
              location.available_to !== AVAILABLE_TO_INDEFINITE
            ) {
              if (selectedIso && selectedIso > location.available_to.slice(0, 10)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: MSG.available_to_exceeds_location,
                  path: ['clinic_locations_a2', index, 'available_to'],
                });
              }
            }
            if (lastVisitDate && selectedIso && selectedIso < lastVisitDate.slice(0, 10)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: MSG.available_to_before_last_visit,
                path: ['clinic_locations_a2', index, 'available_to'],
              });
            }
          }
          // No649: アップ会員の場合、登録日は必須
          if (isUpSystemSite) {
            const rd = (item?.regist_date ?? '').toString().trim();
            if (!rd) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: MSG.regist_date_required,
                path: ['clinic_locations_a2', index, 'regist_date'],
              });
            }
          }
        }
      });
    }
    if (isR2 && hasR2License) {
      locationsR2.forEach((item, index) => {
        if ((item?.clinic_location_id ?? 0) <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: MSG.clinic_location_required,
            path: ['clinic_locations_r2', index, 'clinic_location_id'],
          });
        } else {
          const af = (item?.available_from ?? '').toString().trim();
          if (!af) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: MSG.available_from_required,
              path: ['clinic_locations_r2', index, 'available_from'],
            });
          }
          const at = (item?.available_to ?? '').toString().trim();
          if (af && at) {
            const dFrom = parseDate(af, dayPickerFormat);
            const dTo = parseDate(at, dayPickerFormat);
            if (!Number.isNaN(dFrom.getTime()) && !Number.isNaN(dTo.getTime()) && dTo < dFrom) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: MSG.available_to_after_from,
                path: ['clinic_locations_r2', index, 'available_to'],
              });
            }
          }
          if (at) {
            const selectedIso = dateValueToIso(at);
            const location = clinicLocationOptions.find(l => l.id === item.clinic_location_id);
            if (
              location &&
              location.available_to &&
              location.available_to !== AVAILABLE_TO_INDEFINITE
            ) {
              if (selectedIso && selectedIso > location.available_to.slice(0, 10)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: MSG.available_to_exceeds_location,
                  path: ['clinic_locations_r2', index, 'available_to'],
                });
              }
            }
            if (lastVisitDate && selectedIso && selectedIso < lastVisitDate.slice(0, 10)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: MSG.available_to_before_last_visit,
                path: ['clinic_locations_r2', index, 'available_to'],
              });
            }
          }
          // No649: アップ会員の場合、登録日は必須
          if (isUpSystemSite) {
            const rd = (item?.regist_date ?? '').toString().trim();
            if (!rd) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: MSG.regist_date_required,
                path: ['clinic_locations_r2', index, 'regist_date'],
              });
            }
          }
        }
      });
    }
    // License numbers: global rule = at least one of A2/R2 license fields
    if (a === '' && m === '' && l === '') {
      // Show errors on all three fields so user can choose any one
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: MSG.license_exactly_one,
        path: ['acupuncture_license_number'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: MSG.moxibustion_license_required_for_empty,
        path: ['moxibustion_license_number'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: MSG.license_number_required,
        path: ['license_number'],
      });
    }
  });
}

/** Default schema (A2) for backward compatibility. */
export const createMassagerSchemaDefault = createMassagerSchema(SERVICE_TYPE_A2);

export type CreateMassagerFormData = z.infer<ReturnType<typeof createMassagerSchema>>;
